import { getBrowserClient } from "@/lib/supabase-browser";
import { getAdminClient } from "@/lib/supabase-admin";
import { compressImage } from "@/lib/image-compress";
import type { Tables, Enums } from "@/lib/supabase.types";

export type EventRow = Tables<"events">;
export type EventSignupRow = Tables<"event_signups">;
export type SignupStatus = Enums<"signup_status">;

const DATE_FORMATTER = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const TIME_FORMATTER = new Intl.DateTimeFormat("nb-NO", {
  hour: "2-digit",
  minute: "2-digit",
});

export function formatEventDate(startsAt: string): string {
  return DATE_FORMATTER.format(new Date(startsAt));
}

export function formatEventTime(startsAt: string): string {
  return TIME_FORMATTER.format(new Date(startsAt));
}

/**
 * Resolves an event `image_path` to a usable URL.
 *
 * - `https://…` is returned as-is (legacy or external CDN).
 * - `/foo.jpg` is returned as-is (asset under public/).
 * - Anything else is treated as a key in the `event-images` storage bucket.
 */
export function resolveEventImageUrl(imagePath: string | null): string | null {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
  if (imagePath.startsWith("/")) return imagePath;
  const url = import.meta.env.VITE_SUPABASE_URL?.trim();
  if (!url) return null;
  return `${url.replace(/\/$/, "")}/storage/v1/object/public/event-images/${imagePath.replace(/^\//, "")}`;
}

/** Public listing — only published events, sorted by start time ascending. */
export async function fetchPublishedEvents(): Promise<EventRow[]> {
  const { data, error } = await getBrowserClient()
    .from("events")
    .select("*")
    .eq("is_published", true)
    .order("starts_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** Admin listing — all events including drafts. Requires authenticated admin session. */
export async function fetchAllEvents(): Promise<EventRow[]> {
  const { data, error } = await getAdminClient()
    .from("events")
    .select("*")
    .order("starts_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export type EventWithCount = EventRow & { signup_count: number };

/**
 * Admin listing with aggregated signup counts. Two parallel queries (events
 * + non-cancelled signups), then summed in JS to avoid a Postgres JOIN per
 * request. Anything cancelled doesn't count toward "Påmeldte".
 */
export async function fetchAllEventsWithCounts(): Promise<EventWithCount[]> {
  const supabase = getAdminClient();
  const [eventsRes, signupsRes] = await Promise.all([
    supabase.from("events").select("*").order("starts_at", { ascending: true }),
    supabase.from("event_signups").select("event_id, num_attendees, status"),
  ]);
  if (eventsRes.error) throw eventsRes.error;
  if (signupsRes.error) throw signupsRes.error;

  const counts = new Map<string, number>();
  for (const s of signupsRes.data ?? []) {
    if (s.status === "CANCELLED") continue;
    counts.set(s.event_id, (counts.get(s.event_id) ?? 0) + s.num_attendees);
  }
  return (eventsRes.data ?? []).map((e) => ({
    ...e,
    signup_count: counts.get(e.id) ?? 0,
  }));
}

export async function fetchEvent(id: string): Promise<EventRow | null> {
  const { data, error } = await getAdminClient()
    .from("events")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export interface EventInput {
  title: string;
  slug: string | null;
  starts_at: string;
  duration_minutes: number | null;
  location: string | null;
  body: string | null;
  image_path: string | null;
  signup_href: string | null;
  is_published: boolean;
  sort_order: number;
  enable_signup: boolean;
  max_attendees: number | null;
  signup_deadline: string | null;
}

export async function createEvent(input: EventInput): Promise<EventRow> {
  const { data, error } = await getAdminClient()
    .from("events")
    .insert(input)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateEvent(id: string, input: EventInput): Promise<EventRow> {
  const { data, error } = await getAdminClient()
    .from("events")
    .update(input)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEvent(id: string): Promise<void> {
  const { error } = await getAdminClient().from("events").delete().eq("id", id);
  if (error) throw error;
}

/**
 * URL-vennlig slug fra tittel + dato. Stabil og forutsigbar så vi kan
 * peke `/arrangementer/:slug` til riktig event uten å eksponere id-er.
 */
export function generateEventSlug(title: string, startsAt: string): string {
  const base = title
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "o")
    .replace(/å/g, "a")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  const d = new Date(startsAt);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return base ? `${base}-${date}` : date;
}

// ---------------- Signups ----------------

export interface EventSignupSummary {
  taken: number;
  capacity: number | null;
  is_full: boolean;
}

/** Public capacity info via SECURITY DEFINER RPC. Returns null if event is
 *  not published or doesn't have signup enabled. */
export async function fetchSignupSummary(eventId: string): Promise<EventSignupSummary | null> {
  const { data, error } = await getBrowserClient().rpc("event_signup_summary", {
    p_event_id: eventId,
  });
  if (error) throw error;
  if (!data || data.length === 0) return null;
  const row = data[0];
  return {
    taken: row.taken,
    capacity: row.capacity,
    is_full: row.is_full,
  };
}

export interface SignupSubmission {
  event_id: string;
  name: string;
  email: string;
  phone?: string;
  num_attendees: number;
  message?: string;
}

export async function submitEventSignup(input: SignupSubmission): Promise<void> {
  const res = await fetch("/api/event-signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `Påmelding feilet (${res.status})`);
  }
}

export async function fetchSignupsForEvent(eventId: string): Promise<EventSignupRow[]> {
  const { data, error } = await getAdminClient()
    .from("event_signups")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function updateSignupStatus(id: string, status: SignupStatus): Promise<void> {
  const { error } = await getAdminClient()
    .from("event_signups")
    .update({ status })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteSignup(id: string): Promise<void> {
  const { error } = await getAdminClient().from("event_signups").delete().eq("id", id);
  if (error) throw error;
}

/** Upload a file to event-images and return the storage key. Komprimerer
 *  bildet i browseren før opplasting (resize til maks 1920px, JPEG q85). */
export async function uploadEventImage(file: File): Promise<string> {
  const compressed = await compressImage(file);
  const ext = compressed.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const key = `${crypto.randomUUID()}.${ext}`;
  const { error } = await getAdminClient()
    .storage.from("event-images")
    .upload(key, compressed, { contentType: compressed.type, upsert: false });
  if (error) throw error;
  return key;
}
